import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, ArrowDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const heroImages = ['https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&q=80', 'https://cdn.magicpatterns.com/uploads/254gsweUo5hVSPSHyEupQB/HVAC.jpg', 'https://cdn.magicpatterns.com/uploads/2kN7V4j6E8Ra5mMH83Bzov/mfm.webp', 'https://cdn.magicpatterns.com/uploads/3XW5NpAMJMCuH3srhXLsXz/office.png'];
const navLinks = [{
  label: 'Services',
  route: '/services'
}, {
  label: 'Why AccuTemp',
  route: '/why-accutemp'
}, {
  label: 'Case studies',
  route: '/case-studies'
}, {
  label: 'Resources',
  route: '/knowledge-hub'
}];
export function HeroV2() {
  const [currentImage, setCurrentImage] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  return <section className="relative h-screen min-h-[700px] overflow-hidden" data-id="element-159">
      {/* Background images — z-0 */}
      <div className="absolute inset-0" data-id="element-160">
        <AnimatePresence mode="popLayout" data-id="element-161">
          <motion.img key={currentImage} src={heroImages[currentImage]} alt="Commercial HVAC installation" className="absolute inset-0 w-full h-full object-cover" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 1.2,
          ease: 'easeInOut'
        }} data-id="element-162" />
        </AnimatePresence>
      </div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-primary-dark/55" data-id="element-163"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/70 via-primary-dark/20 to-primary-dark/60" data-id="element-164"></div>

      {/* All foreground content — single z-layer above overlays */}
      <div className="relative z-10 h-full flex flex-col" data-id="element-165">
        {/* Nav bar — Sparc layout: links left, logo center, CTA right */}
        <motion.nav initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="w-full pt-8 pb-4 px-8 md:px-12 lg:px-16" data-id="element-166">
          <div className="flex items-center justify-between relative" data-id="element-167">
            {/* Left — nav links (desktop) */}
            <div className="hidden lg:flex items-center gap-8 text-sm font-medium text-white" data-id="element-168">
              {navLinks.map(link => link.route ? <Link key={link.label} to={link.route} className="hover:text-white transition-colors" data-id="element-169">
                    {link.label}
                  </Link> : <a key={link.label} href={link.anchor} className="hover:text-white transition-colors" data-id="element-170">
                    {link.label}
                  </a>)}
            </div>

            {/* Mobile burger (left side) */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-white" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} data-id="element-171">
              {mobileMenuOpen ? <XIcon size={26} data-id="element-172" /> : <MenuIcon size={26} data-id="element-173" />}
            </button>

            {/* Center — logo */}
            <Link to="/home-v2" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-174">
              <img src="https://cdn.magicpatterns.com/uploads/m22cd52VGFAbga9BNLUyHJ/accutemp-logo-white.svg" alt="AccuTemp Air Conditioning" className="h-9 md:h-11 w-auto" data-id="element-175" />
            </Link>

            {/* Right — CTA */}
            <Link to="/contact" className="hidden lg:inline-flex border border-white/30 hover:bg-white hover:text-primary-dark text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300" data-id="element-176">
              Contact us
            </Link>

            {/* Mobile right spacer */}
            <div className="lg:hidden w-8" data-id="element-177"></div>
          </div>
        </motion.nav>

        {/* Mobile slide menu */}
        <AnimatePresence data-id="element-178">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="lg:hidden overflow-hidden px-8 md:px-12" data-id="element-179">
              <div className="flex flex-col gap-4 pb-6 border-b border-white/10" data-id="element-180">
                {navLinks.map(link => link.route ? <Link key={link.label} to={link.route} onClick={() => setMobileMenuOpen(false)} className="text-lg text-white/80 hover:text-white transition-colors" data-id="element-181">
                      {link.label}
                    </Link> : <a key={link.label} href={link.anchor} onClick={() => setMobileMenuOpen(false)} className="text-lg text-white/80 hover:text-white transition-colors" data-id="element-182">
                      {link.label}
                    </a>)}
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="inline-flex w-fit bg-accent hover:bg-accent/90 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors mt-2" data-id="element-183">
                  Contact us
                </Link>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Hero content — center-left, matching Sparc positioning */}
        <div className="flex-1 flex flex-col justify-center px-8 md:px-12 lg:px-16" data-id="element-184">
          <div className="max-w-4xl" data-id="element-185">
            <motion.h1 initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.9,
            delay: 0.3
          }} className="text-5xl md:text-7xl lg:text-8xl text-white font-serif leading-[1.05] mb-8 tracking-tight" data-id="element-186">
              Commercial AC
              <br data-id="element-187" />
              installation &<br data-id="element-188" />
              maintenance
            </motion.h1>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} data-id="element-189">
              <a href="#contact" className="group inline-flex items-center gap-4 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white pl-6 pr-2 py-2 rounded-full transition-all duration-300" data-id="element-190">
                <span className="text-sm font-medium" data-id="element-191">Get a free quote</span>
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-192">
                  <PlayIcon className="w-4 h-4 text-white ml-0.5 fill-white" data-id="element-193" />
                </div>
              </a>
            </motion.div>
          </div>
        </div>

        {/* Bottom bar — tagline left, discover more right */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.8
      }} className="px-8 md:px-12 lg:px-16 pb-10 flex justify-between items-end" data-id="element-194">
          <div className="text-white/60 text-sm leading-relaxed" data-id="element-195">
            Nationwide Daikin D1+ Premier Partners.
            <br data-id="element-196" />
            Based in Coventry, West Midlands.
          </div>

          <a href="#partner-logos" className="hidden md:flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors" data-id="element-197">
            <span data-id="element-198">Discover more</span>
            <ArrowDownIcon className="w-4 h-4 animate-bounce" data-id="element-199" />
          </a>
        </motion.div>
      </div>
    </section>;
}